import { AfterViewInit, Component, ElementRef, HostListener, OnChanges, OnInit, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { CommonModule, formatNumber } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { BaseComponent } from '../../base/base.component';
import { FunctionService } from '../../services/function.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, forkJoin, Observable } from 'rxjs';
import { ColumnCustomizeComponent, TableColumn } from '../column-customize/column-customize.component';
import { OrderByPipe } from '../../base/order-by.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ErrorHelperService } from '../../services/error-helper.service';
import { NotificationService } from '../../services/notification.service';
import { Carrier } from '../../interfaces/anagraphic.interfaces';
import { ShipmentManagementService } from '../../services/shipment-management.service';
import { WaybillService } from '../../services/waybill.service';
import { SearchableSelectComponent, SearchableSelectOption } from '../../components/searchable-select/searchable-select.component';
import SignaturePad from 'signature_pad';
import { OnDestroy } from '@angular/core';
import { Dealer } from '../trackingDetails/trackingDetails.component';
import { TourRouting } from '../../interfaces/TourRouting.interfaces';

interface Region {
  regionCode: string;
  regionName?: string;
}

export interface ViewParcels {
  parcelWaybillCode: string;
  parcelCode: string;
  parcelDate: string;
  parcelDeliveryDate: string;
  parcelEstimatedDeliveryDate: string;
  parcelDealerCode: string;
  dealerName: string;
  dealerOperatingAddressStreet: string;
  dealerOperatingAddressCity: string;
  dealerLatitude: number;
  dealerLongitude: number;
  parcelDescription: string;
  parcelDeliveryType: string;
  parcelWeight: number;
  parcelVolume: number;
  parcelBarcode: string;
  parcelTrackingNumber: string;
  parcelLatitude: number;
  parcelLongitude: number;
  parcelNotifiedStatus: string;
  parcelDeliveryStatus: string;
  parcelModifyManual: string;
  distanceInMeters: number;
  coordinatesInRange: string;
  parcelWaybillCarrierCode: string;
  parcelWaybillCarrierName: string;
  parcelWaybillOriginLocation: string;
  parcelWaybillLastMileOrigin: string;
  parcelWaybillDeliveryMethod: string;
  parcelRegionCode: string;
  parcelRegionName: string;
  showTrackings?: boolean;
  parcelWaybillDeliveryType: string;
  parcelDeliveryReason: string;
  parcelOnTime: string;
  parcelOriginalDeliveryDate: string;
  parcelOriginalLatitude: number;
  parcelOriginalLongitude: number;
  parcelDeliveryDateIsForced: string;
  parcelDeliveryDateIsUpdated: string;
  parcelDeliveryCoordinatesIsForced: string;
  parcelDeliveryCoordinatesIsUpdated: string;
  parcelWaybillLastKnownLocation: string;
  parcelBrandCode: string;
  parcelClientCode: string;
  brandName: string;
  clientName: string;
  minutesDelay: number | null;
  waybillScheduleType: string;
  distanceOriginalInMeters: number;
  parcelJustificationId: number;
  parcelJustificationComment: string;
  parcelWaybillLoadingBay: string;
  parcelWaybillStatus: string;
  isRoutingPresent: string;
  parcelCurrentWarehouse: string;
  parcelWaybillMaximumRange: number;
  parcelWarehouseExternalCode: string;
  parcelDetailStatus: string;
  tourRoutingExternal: string;
  parcelOriginalWaybillCode: string;
  parcelHasMisrouting: string;
  waybillDdtAttachments: string;
  isRestrictedClient: string;
  waybillShipmentCode: string;
  clientRequestSignature?: string;
  parcelType?: string;
  parcelPalletType?: string;
  parcelPalletDescription?: string;
  isBigOrder?: boolean;
  parcelDeliveryChangeCutoffDate?: string;
  parcelDeliveryChangeCutoffCountdown?: string;
  parcelDeliveryChangeCutoffExpired?: boolean;
  parcelDeliveryChangeCutoffWarning?: boolean;
  parcelLength?: number;
  parcelWidth?: number;
  parcelHeight?: number;
  waybillDdtSentDate?: string;
  waybillDownloadDdtDate?: string;
  waybillDownloadDdtBy?: string;
  parcelTransitStatus?: string;
  parcelTourGroupName?: string;
  parcelPickingEventStart?: string;
  parcelShuttleArrivalDate?: string;
}

export interface WaybillGroup {
  waybillCode: string;
  waybillOriginalCode: string;
  waybillShipmentCode: string;
  clientCode: string;
  clientName: string;
  dealerName: string;
  dealerOperatingAddressStreet: string;
  dealerOperatingAddressCity: string;
  parcelRegionName: string;
  parcelDate: string;
  parcelEstimatedDeliveryDate: string;
  parcelDeliveryDate: string;
  waybillStatus: string;
  ddtCodes: string;
  totalWeight: number;
  totalVolume: number;
  parcelsCount: number;
  parcels: ViewParcels[];
  waybillDdtSentDate?: string;
  waybillDownloadDdtDate?: string;
  waybillDownloadDdtBy?: string;
  parcelTransitStatus?: string;
  parcelTourGroupName?: string;
  parcelOriginalDeliveryDate?: string;
  parcelPickingEventStart?: string;
  parcelShuttleArrivalDate?: string;
}

export type PlatformTransitStatus = 'ARRIVING' | 'ARRIVED' | 'CLOSED' | 'DELIVERED' | 'DELIVERED_NO_OUTCOME';

export interface TourGroupOption {
  name: string;
}

export interface OverviewWarehouseSummary {
  totalParcels: number;
  totalWeight: number;
  totalVolume: number;
  totalWaybills: number;
}

interface Warehouse {
  warehouseCode: string;
  warehouseName?: string;
  warehouseShortName?: string;
  warehouseType?: string;
}

interface Justification {
  justificationId: number;
  justificationName?: string;
}

interface Client {
  clientCode: string;
  clientName?: string;
}

interface Brand {
  brandCode: string;
  brandName?: string;
}

interface ClientsBrands {
  clientCode: string;
  clientName?: string;
  brands: Brand[];
}

export enum ParcelDetailStatus {
  PREPARED = 'Preparata',
  IN_TRIP_TO_TP = 'In viaggio verso TP',
  IN_TRIP = 'In viaggio verso dealer',
  CLOSED = 'Chiuso',
  SUSPENDED = 'Sospeso'
}

type OverviewDateField = 'parcelDateStart' | 'parcelDateEnd' | 'parcelEstimatedDeliveryDateStart' | 'parcelEstimatedDeliveryDateEnd' | 'shuttleArrivalDateStart' | 'shuttleArrivalDateEnd';

@Component({
  selector: 'app-overview-warehouse',
  standalone: true,
  templateUrl: './overview-warehouse.component.html',
  styleUrls: ['./overview-warehouse.component.css'],
  imports: [FormsModule, CommonModule, TranslateModule, SearchableSelectComponent, ColumnCustomizeComponent, OrderByPipe, MatTooltipModule]
})
export class OverviewWarehouseComponent extends BaseComponent implements OnInit  {

  @ViewChild('signatureCanvas') signatureCanvas?: ElementRef<HTMLCanvasElement>;

  showImportExcelModal = false;
  selectedExcelFile!: File;

  private countdownInterval: any = null;

  private signaturePad!: SignaturePad;

  signaturePreview: string | null = null;
  signatureError: string | null = null;
  private signatureInitialized = false;
  isReplacingSignature = false;

  parcelSignature: string | null = null;

  ParcelDetailStatus = ParcelDetailStatus;

  numberParcelsOfWaybillNotOnTime: number = 0;
  numberParcelsOfWaybill: number = 0;
  currentPage: number = 1;
  itemsPerPage: number = 25;

  parcels: ViewParcels[] = [];
  filteredParcels: ViewParcels[] = [];

  temp: ViewParcels[] = [];

  successDeliveryPropagation = false;
  errorDeliveryPropagation: string | null = null;

  errorDeliveryNull: boolean = false;

  searchDealerCode: string = '';
  searchDDTCodes: string = '';
  searchParcelCode: string = '';
  searchParcelWaybillCode: string = '';
  parcelDateStart: string = '';
  parcelDateEnd: string = '';

  showUndefinedParcels: boolean = false;
  showBigOrders: boolean = false;

  parcelEstimatedDeliveryDateStart: string = '';
  parcelEstimatedDeliveryDateEnd: string = '';

  shuttleArrivalDateStart: string = '';
  shuttleArrivalDateEnd: string = '';

  scheduleType: string = '';
  scheduleIsNight: string = '';

  dateError: string = '';
  isLoading: boolean = false;

  loadingComParcels = false;
  errorLoading = false;

  totalRecords: number = 0;

  isCoordinatesCorrectSelectedParcel: boolean = false;
  showButtonCreateCoordinates: boolean = false;

  globalSearchTerm: string = '';
  notificationStatus: string = ''; // "not_notified" | "notified"
  deliveryStatus: string = '';     // "delivered" | "not_delivered"
  ddtReceivedStatus: string = '';  // "RECEIVED" | "NOT_RECEIVED"
  ddtPrintedStatus: string = '';   // "DOWNLOADED" | "NOT_DOWNLOADED"
  inRangeStatus: string = '';
  modifyStatus: string = ''; // modified | not_modified
  withProblemsStatus: string = '';
  onTimeStatus: string = '';
  justificationStatus: string = '';
  misroutingStatus: string = '';

  showFilters: boolean = false;

  selectedParcel: ViewParcels | null = null;
  showModal: boolean = false;
 
  showModalDetailModifies: boolean = false;
 
  showNotification = false;
  notificationMessage = '';
 
  showRollbackModal = false;
  rollbackChoice: 'parcel' | 'waybill' | null = null;

  editChoice: 'parcel' | 'waybill' | null = null;
 
  editForm: any = {
    parcelDate: '',
    parcelDeliveryDate: '',
    parcelDescription: '',
    parcelWeight: 0,
    parcelVolume: 0,
    parcelLatitude: null as number | null,
    parcelLongitude: null as number | null
  };

  restrictedMinutes: number | null = null;

  groupedWaybills: WaybillGroup[] = [];

  private serverWaybillTotals = new Map<string, { totalWeight: number; totalVolume: number; parcelsCount: number }>();

  parcelDetailStatusOptions = [
    { value: 'PREPARED', label: ParcelDetailStatus.PREPARED },
    { value: 'IN_TRIP_TO_TP', label: ParcelDetailStatus.IN_TRIP_TO_TP },
    { value: 'IN_TRIP', label: ParcelDetailStatus.IN_TRIP },
    { value: 'CLOSED', label: ParcelDetailStatus.CLOSED },
    { value: 'SUSPENDED', label: ParcelDetailStatus.SUSPENDED }
  ];

  filteredWarehouses: Warehouse[] = [];
  filteredWarehousesFrom: Warehouse[] = [];
  warehouseCode: string = '';
  warehouseFromCode: string = '';

  filteredBrands: Brand[] = [];

  selectedDealerCode: string = '';
  filteredDealers: Dealer[] = [];

  selectedCarrierCode: string = '';
  filteredCarriers: Carrier[] = [];

  selectedRegionCode: string = '';
  filteredRegions: Region[] = [];

  selectedDeliveryType: string = '';
  filteredTours: TourRouting[] = [];

  readonly platformStatusOptions: { value: PlatformTransitStatus; label: string; icon: string }[] = [
    { value: 'ARRIVING',  label: 'In Arrivo',   icon: 'fa-truck-loading' },
    { value: 'ARRIVED',   label: 'Arrivati',    icon: 'fa-warehouse' },
    { value: 'CLOSED',    label: 'Usciti',      icon: 'fa-truck' },
    { value: 'DELIVERED', label: 'Consegnati',  icon: 'fa-circle-check' },
    { value: 'DELIVERED_NO_OUTCOME', label: 'Consegnati ma non esitati', icon: 'fa-hourglass-half' }
  ];
  selectedStatusFilters: PlatformTransitStatus[] = ['ARRIVING'];

  readonly LAST_DAYS_MAX = 60;
  lastDaysBack: number = 7;
  private lastAppliedDaysBack: number = 7;

  tourGroupOptions: TourGroupOption[] = [];
  tourGroupSelectOptions: SearchableSelectOption[] = [];
  selectedTourGroupNames: string[] = [];

  columnFilters: { [key: string]: string } = {};
  openColumnFilterKey: string | null = null;
  columnFilterDraft: string = '';

  showDdtSentModal: boolean = false;
  showDdtDownloadModal: boolean = false;
  selectedWaybillGroupForModal: WaybillGroup | null = null;

  filterOnlyWithCoordsAndNoDeliveryDate: string = '';

  sortState = { active: 'parcelDate', direction: 'desc' };

  selectedBrands: string[] = [];
  filteredClients: Client[] = [];
  selectedClients: string[] = [];
  clientsBrands: ClientsBrands[] = [];

  selectedDeliveryMethod: string = '';


  showJustificationModal: boolean = false;

  editFormJustification: any = {
    parcelJustificationId: '',
    parcelJustificationComment: ''
  };

  selectedCarrierCodes: string[] = [];
  selectedWarehouseFromCodes: string[] = [];
  selectedRegionCodes: string[] = [];
  selectedParcelDetailStatusValues: string[] = [];

  clientSelectOptions: SearchableSelectOption[] = [];
  brandSelectOptions: SearchableSelectOption[] = [];
  dealerSelectOptions: SearchableSelectOption[] = [];
  carrierSelectOptions: SearchableSelectOption[] = [];
  warehouseFromSelectOptions: SearchableSelectOption[] = [];
  warehouseSelectOptions: SearchableSelectOption[] = [];
  regionSelectOptions: SearchableSelectOption[] = [];
  tourSelectOptions: SearchableSelectOption[] = [];
  parcelDetailStatusSelectOptions: SearchableSelectOption[] = [];

  readonly notificationOptions: SearchableSelectOption[] = [
    { value: 'not_notified', label: 'Solo colli non notificati' },
    { value: 'notified', label: 'Solo colli notificati' }
  ];
  readonly deliveryOptions: SearchableSelectOption[] = [
    { value: 'not_delivered', label: 'Solo colli non consegnati' },
    { value: 'delivered', label: 'Solo colli consegnati' }
  ];
  readonly ddtReceivedOptions: SearchableSelectOption[] = [
    { value: 'RECEIVED', label: 'Solo bolle ricevute' },
    { value: 'NOT_RECEIVED', label: 'Solo bolle non ricevute' }
  ];
  readonly ddtPrintedOptions: SearchableSelectOption[] = [
    { value: 'DOWNLOADED', label: 'Solo bolle stampate' },
    { value: 'NOT_DOWNLOADED', label: 'Solo bolle non stampate' }
  ];
  readonly modifyOptions: SearchableSelectOption[] = [
    { value: 'not_modified', label: 'Solo colli non armonizzati' },
    { value: 'modified', label: 'Solo colli armonizzati' }
  ];
  readonly onTimeOptions: SearchableSelectOption[] = [
    { value: 'ON_TIME', label: 'Colli in orario' },
    { value: 'NOT_ON_TIME', label: 'Colli in ritardo' },
    { value: 'DELAY_LT_2H', label: '\u25B9 Ritardo \u2264 2h' },
    { value: 'DELAY_2H_3H', label: '\u25B9 2h < ritardo \u2264 3h' },
    { value: 'DELAY_3H_1D', label: '\u25B9 3h < ritardo \u2264 1 giorno' },
    { value: 'DELAY_GT_1D', label: '\u25B9 Ritardo > 1 giorno' }
  ];
  readonly inRangeOptions: SearchableSelectOption[] = [
    { value: 'NOT_IN_RANGE', label: 'Solo coordinate errate' },
    { value: 'IN_RANGE', label: 'Solo coordinate corrette' }
  ];
  readonly scheduleTypeOptions: SearchableSelectOption[] = [
    { value: 'AxA', label: 'DMDC' },
    { value: 'AxB', label: 'A*B' },
    { value: 'AxC', label: 'A*C' }
  ];
  readonly nightDayOptions: SearchableSelectOption[] = [
    { value: 'Y', label: 'Night' },
    { value: 'N', label: 'Day' }
  ];
  readonly deliveryMethodOptions: SearchableSelectOption[] = [
    { value: 'BY_ROAD', label: 'Via terra' },
    { value: 'BY_AIR', label: 'Via aerea' }
  ];

  @ViewChild('parcelDateStartInput') parcelDateStartInput?: ElementRef<HTMLInputElement>;
  @ViewChild('parcelDateEndInput') parcelDateEndInput?: ElementRef<HTMLInputElement>;
  @ViewChild('parcelEstimatedDeliveryDateStartInput') parcelEstimatedDeliveryDateStartInput?: ElementRef<HTMLInputElement>;
  @ViewChild('parcelEstimatedDeliveryDateEndInput') parcelEstimatedDeliveryDateEndInput?: ElementRef<HTMLInputElement>;
  @ViewChild('shuttleArrivalDateStartInput') shuttleArrivalDateStartInput?: ElementRef<HTMLInputElement>;
  @ViewChild('shuttleArrivalDateEndInput') shuttleArrivalDateEndInput?: ElementRef<HTMLInputElement>;

  private datePickers = new Map<OverviewDateField, FlatpickrInstance>();

  dateErrorScope: 'affido' | 'stimata' | 'entrambi' | null = null;

  warehouseError: boolean = false;

  readonly pageSizeOptions: number[] = [5, 10, 25, 50, 100];

  selectedWaybills = new Map<string, string>();
  allMatchingSelected: boolean = false;
  isSelectingAll: boolean = false;
  isPrintingPDF: boolean = false;
  isExportingExcel: boolean = false;
  isDownloadingEdi: boolean = false;

  expandedWaybills = new Set<string>();

  summaryReport: OverviewWarehouseSummary = {
    totalParcels: 0,
    totalWeight: 0,
    totalVolume: 0,
    totalWaybills: 0,
  };
  isLoadingSummaryReport = false;
  isReportCollapsed = false;

  expandedWaybillCodes = new Set<string>();

  expandAllMode: boolean | null = null;

  columns: TableColumn[] = [
    
        { field: "parcelWaybillStatus", label: "Stato Bolla", visible: true, order: 2, specialOnly: true, sortable: true },
        { field: "parcelDetailStatus", label: "Stato", visible: true, order: 3, sortable: true },
        { field: "clientName", label: "Cliente", visible: true, order: 6, sortable: true },
        { field: "brandName", label: "Marchio", visible: true, order: 7, sortable: true },
        { field: "parcelCode", label: "Codice Collo", visible: true, order: 8, sortable: true },
        { field: "parcelWaybillCode", label: "Codice Nuova Bolla", visible: true, order: 9, sortable: true, specialOnly: true },
        { field: "parcelOriginalWaybillCode", label: "Codice Bolla", visible: true, order: 10, sortable: true },
        { field: "waybillDdtAttachments", label: "Codici DDT", visible: true, order: 11, sortable: true },
        // { field: "parcelWaybillCodeFull", label: "Codice Spedizione", visible: true, order: 7, sortable: true },
        { field: "parcelDate", label: "Data Affido", visible: true, order: 12, sortable: true },
        { field: "parcelWaybillOriginLocation", label: "Magazzino partenza", visible: true, order: 13, sortable: true },
        { field: "parcelWaybillCarrierName", label: "Vettore", visible: true, order: 14, specialOnly: true, sortable: true },
        { field: "parcelDealerCode", label: "Codice Destinatario", visible: true, order: 15, sortable: true },
        { field: "dealerName", label: "Destinatario", visible: true, order: 20, sortable: true },
        { field: "dealerOperatingAddressStreet", label: "Indirizzo destinatario", visible: true, order: 23, sortable: true },
        { field: "dealerOperatingAddressCity", label: "Città destinatario", visible: true, order: 24 },
        { field: "parcelRegionName", label: "Regione", visible: true, order: 25, sortable: true },
        { field: "parcelDeliveryDate", label: "Data Consegna", visible: true, order: 4, sortable: true },
        { field: "parcelEstimatedDeliveryDate", label: "Stima Consegna", visible: true, order: 5, sortable: true },
        { field: "parcelOnTime", label: "In Orario", visible: true, order: 32, specialOnly: true, sortable: true },
        { field: "minutesDelay", label: "Ritardo (min)", visible: true, order: 36, sortable: true},
        { field: "parcelWaybillDeliveryMethod", label: "Tipologia Consegna", visible: true, order: 37, specialOnly: true, sortable: true },
        { field: "tourRoutingExternal", label: "Tour", visible: true, order: 39, sortable: true },
        { field: "parcelDeliveryReason", label: "Causale di consegna", visible: true, order: 42, sortable: true },
        // { field: "parcelLatitude", label: "Lat", visible: true, order: 43 },
        // { field: "parcelLongitude", label: "Lng", visible: true, order: 44 },
        // { field: "distanceInMeters", label: "Distanza (m)", visible: true, order: 45, sortable: true },
        // { field: "pod", label: "POD", visible: true, order: 49 },
  ];

  constructor(
    public http: HttpClient,
    functionService: FunctionService,
    router: Router,
    private notificationService: NotificationService,
    private errorHelperService: ErrorHelperService,
    private route: ActivatedRoute,
    private shipmentManagementService: ShipmentManagementService,
    private waybillService: WaybillService,
  ) {
    super(functionService, router);
  }


  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const qpDeliveryStatus = params.get('deliveryStatus');
    const qpOnTimeStatus = params.get('onTimeStatus');
    const qpRegionCode = params.get('regionCode');
    const qpParcelDateStart = params.get('parcelDateStart');
    const qpParcelDateEnd = params.get('parcelDateEnd');
    const qpParcelEstimetedDateStart = params.get('parcelEstimetedDateStart');
    const qpParcelEstimetedDateEnd = params.get('parcelEstimetedDateEnd');
    const qpParcelWithProblemsStatus = params.get('withProblemsStatus');
    const qpInRangeStatus = params.get('inRangeStatus');
    // Link da borderò (colli aggiunti in arrivo): magazzino e codice collo gia' filtrati
    const qpWarehouseCode = params.get('warehouseCode');
    const qpParcelCode = params.get('parcelCode');

    if (qpDeliveryStatus) {
      this.deliveryStatus = qpDeliveryStatus;
    }

    if (qpOnTimeStatus) {
      this.onTimeStatus = qpOnTimeStatus;
    }

    if (qpParcelWithProblemsStatus) {
      this.withProblemsStatus = qpParcelWithProblemsStatus;
    }

    if (qpRegionCode) {
      this.selectedRegionCodes = [qpRegionCode];
    }

    if (qpParcelDateStart) {
      this.parcelDateStart = qpParcelDateStart;
    }

    if (qpParcelDateEnd) {
      this.parcelDateEnd = qpParcelDateEnd;          
    }

    if (qpParcelEstimetedDateStart) {
      this.parcelEstimatedDeliveryDateStart = qpParcelEstimetedDateStart;      
    }

    if (qpParcelEstimetedDateEnd) {
      this.parcelEstimatedDeliveryDateEnd = qpParcelEstimetedDateEnd;          
    }

    if (qpInRangeStatus) {
      this.inRangeStatus = qpInRangeStatus;
    }

    if (qpWarehouseCode) {
      // Validato dopo il caricamento dei magazzini da autoSelectSingleWarehouse()
      this.warehouseCode = qpWarehouseCode;
    }

    if (qpParcelCode) {
      this.searchParcelCode = qpParcelCode;
      // Ricerca puntuale di un collo: il collo puo' essere in qualsiasi stato (di default solo
      // "In Arrivo") e affidato da piu' dei 7 giorni di default, quindi allargo stati e giorni
      this.selectedStatusFilters = this.platformStatusOptions.map(o => o.value);
      this.lastDaysBack = this.LAST_DAYS_MAX;
      this.lastAppliedDaysBack = this.LAST_DAYS_MAX;
    }

    const saved = localStorage.getItem("parcelTableConfig");
    if (saved) {
      this.columns = JSON.parse(saved);
    }
    this.columns = this.columns.filter(col => this.isColumnAllowed(col) && col.field !== 'actions');

    this.columns.sort((a,b) => a.order - b.order);

    const savedPage = localStorage.getItem('currentParcelPage');
    this.currentPage = savedPage ? +savedPage : 1;

    const savedItemsPerPage = localStorage.getItem('itemsPerPage');
    this.itemsPerPage = savedItemsPerPage ? +savedItemsPerPage : 25;

    this.parcels = [];
    this.filteredParcels = [];

    const stored = localStorage.getItem('userFunctions');
    if(stored) {
      const parsed = JSON.parse(stored);
      this.clientsBrands = parsed.clients;

      if (
      this.clientsBrands.length === 1 &&
      this.clientsBrands[0].clientCode === 'CLT00001'
      ) {
        this.showBigOrders = true;
      }

      this.filteredClients = this.clientsBrands.map(cb => ({
        clientCode: cb.clientCode,
        clientName: cb.clientName,
      }))
      .sort((a, b) => {
        if (!a.clientName && !b.clientName) return 0;       
        if (!a.clientName) return 1;                        
        if (!b.clientName) return -1;                       
        return a.clientName.localeCompare(b.clientName);   
      });

      const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
        cb.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }))
      ).sort((a, b) => {
        if (!a.brandName && !b.brandName) return 0;       
        if (!a.brandName) return 1;                        
        if (!b.brandName) return -1;                       
        return a.brandName.localeCompare(b.brandName);   
      });

      this.filteredBrands = Array.from(
        new Map(allBrands.map(b => [b.brandCode, b])).values()
      );

      this.rebuildSelectOptions();

      forkJoin({
              warehouses: this.http.get<Warehouse[]>(`${environment.apiUrl}/overview-warehouse/warehouses`),
              carriers: this.http.get<Carrier[]>(`${environment.apiUrl}/overview-warehouse/carriers`),
              dealers: this.http.post<Dealer[]>(`${environment.apiUrl}/overview-warehouse/dealers`, this.clientsBrands),
              regions: this.http.get<Region[]>(`${environment.apiUrl}/overview-warehouse/regions`),
            }).subscribe({
              next: (results) => {
                if(parsed.warehouses != null && parsed.warehouses.length === 1) {
                  let singleWarehouse = parsed.warehouses[0];
                  this.filteredWarehouses = results.warehouses.filter(w => w.warehouseCode === singleWarehouse);
                }
                else {
                  this.filteredWarehouses = results.warehouses;
                }
                this.filteredWarehousesFrom = results.warehouses.filter(w => w.warehouseType?.split(';').includes('CDR'));
                this.filteredCarriers = results.carriers;
                this.filteredDealers = results.dealers;
                this.filteredRegions = results.regions;
                this.rebuildSelectOptions();
                this.autoSelectSingleWarehouse();
                this.loadTourGroups();
                this.applyFilters();

                //this.loadData();
              },
              error: (err) => {
                console.error('Errore nel caricamento iniziale:', err);
              }
            });
          }
  }

  shouldHideBigOrdersToggle(): boolean {
    return !!this.clientsBrands &&
          this.clientsBrands.length === 1 &&
          this.clientsBrands[0]?.clientCode === 'CLT00001';
  }

  private isColumnAllowed(col: any): boolean {
    if (col.specialOnly && !this.canSpecial()) {
      return false;
    }
    else if (col.updatableOnly && (!this.canSpecial() || !this.canUpdate())) {
      return false;
    }
    return true;
  }

  onClientsChange(): void {
    this.getIntersectionClientsBrands();
  }

  getIntersectionClientsBrands() : void {
    if (!this.selectedClients || this.selectedClients.length === 0) {
      // Nessun client selezionato → mostro tutti i brands
      this.filteredBrands = Array.from(
        new Map(
          this.clientsBrands
            .flatMap(cb => cb.brands)
            .map(b => [b.brandCode, b])
        ).values()
      );
      this.selectedBrands = [];
      this.rebuildSelectOptions();
      return;
    }

    const brandLists = this.selectedClients.map(code => {
      const client = this.clientsBrands.find(cb => cb.clientCode === code);
      return client ? client.brands : [];
    });

    const intersection = brandLists.reduce((acc, curr) => {
      return acc.filter(b =>
        curr.some(x => x.brandCode === b.brandCode)
      );
    });

    this.filteredBrands = intersection;
    this.selectedBrands = this.selectedBrands.filter(code =>
      intersection.some(b => b.brandCode === code)
    );
    this.rebuildSelectOptions();
  }



    loadBrands(): void {
      this.http.get<Brand[]>(`${environment.apiUrl}/shipment-request/brands`).subscribe({
        next: (data) => this.filteredBrands = data,
        error: () => console.error('Errore nel caricamento dei brands')
      });
    }

  onItemsPerPageChange(): void {
    this.currentPage = 1;
    localStorage.setItem('itemsPerPage', this.itemsPerPage.toString());
  }

  resetItemsPerPage(): void {
    this.itemsPerPage = 25;
    this.currentPage = 1;
    localStorage.setItem('itemsPerPage', '25');
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.getTotalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    localStorage.setItem('currentParcelPage', page.toString());
    this.expandedWaybills.clear();
    this.loadData(page);
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.globalSearchTerm = '';
    this.clearDateError();
    this.clearSelection();
    this.expandedWaybills.clear();
    this.summaryReport = { totalParcels: 0, totalWeight: 0, totalVolume: 0, totalWaybills: 0 };

    this.warehouseError = !this.warehouseCode;
    if (this.warehouseError) {
      this.parcels = [];
      this.filteredParcels = [];
      this.groupedWaybills = [];
      this.totalRecords = 0;
      return;
    }

    const hasStartDate = !!this.parcelDateStart;
    const hasEndDate = !!this.parcelDateEnd;


    if (hasStartDate && hasEndDate && new Date(this.parcelDateStart) > new Date(this.parcelDateEnd)) {
      this.setDateError(
        `"Affido Da" (${this.getFormattedDate('parcelDateStart')}) è successiva ad "Affido A" (${this.getFormattedDate('parcelDateEnd')}): correggi l'intervallo.`,
        'affido');
      this.parcels = [];
      this.filteredParcels = [];
      this.groupedWaybills = [];
      this.totalRecords = 0;
      return;
    }

    this.loadData();
  }

  private buildClientsBrandsSelected(): ClientsBrands[] {
    let clientsBrandsSelected: ClientsBrands[];

    if (this.filteredClients.length == 1) {
      this.selectedClients = this.filteredClients.map(c => c.clientCode);
    }

    if (this.selectedClients.length > 0) {
      clientsBrandsSelected = this.selectedClients.map(clientCode => {
        const originalClient = this.clientsBrands.find(cb => cb.clientCode === clientCode);

        let filteredBrandsFromClient: Brand[] = [];
        if (this.selectedBrands.length > 0) {
          filteredBrandsFromClient = (originalClient?.brands || []).filter(b =>
            this.selectedBrands.includes(b.brandCode)
          );
        } else {
          filteredBrandsFromClient = this.filteredBrands;
        }

        return {
          clientCode: clientCode,
          clientName: originalClient?.clientName,
          brands: filteredBrandsFromClient
        } as ClientsBrands;
      });
    } else if (this.selectedBrands && this.selectedBrands.length > 0) {
      clientsBrandsSelected = this.clientsBrands
        .map(cb => {
          const matchingBrands = cb.brands.filter(b =>
            this.selectedBrands.includes(b.brandCode)
          );
          if (matchingBrands.length > 0) {
            return {
              clientCode: cb.clientCode,
              clientName: cb.clientName,
              brands: matchingBrands
            } as ClientsBrands;
          }
          return null;
        })
        .filter(cb => cb !== null) as ClientsBrands[];
    } else {
      clientsBrandsSelected = this.clientsBrands;
    }

    return clientsBrandsSelected;
  }

  onLastDaysBackChange(): void {
    let value = Math.floor(Number(this.lastDaysBack));
    if (!Number.isFinite(value) || value < 0) value = 0;
    if (value > this.LAST_DAYS_MAX) value = this.LAST_DAYS_MAX;
    this.lastDaysBack = value;

    if (value === this.lastAppliedDaysBack) return;
    this.lastAppliedDaysBack = value;
    this.applyFilters();
  }

  private toLocalIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private getEffectiveParcelDateRange(): { start: string; end: string } {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - this.lastAppliedDaysBack);

    const todayStr = this.toLocalIsoDate(today);
    const fromStr = this.toLocalIsoDate(from);

    const manualStart = (this.parcelDateStart || '').substring(0, 10);
    const manualEnd = (this.parcelDateEnd || '').substring(0, 10);

    return {
      start: manualStart && manualStart > fromStr ? manualStart : fromStr,
      end: manualEnd && manualEnd < todayStr ? manualEnd : todayStr
    };
  }

  private buildSearchBody(page: number, pageSize: number): any {
    const parcelDateRange = this.getEffectiveParcelDateRange();
    return {
      clients: this.buildClientsBrandsSelected() || [],

      parcelWaybillCode: this.searchParcelWaybillCode || '',
      parcelCode: this.searchParcelCode || '',
      parcelDateStart: parcelDateRange.start,
      parcelDateEnd: parcelDateRange.end,
      parcelDealerCodes: [],
      carrierCodes: [],
      warehouseCode: this.warehouseCode,
      warehouseCodes: this.warehouseCode ? [this.warehouseCode] : [],
      warehouseFromCodes: this.selectedWarehouseFromCodes,

      regionCodes: this.selectedRegionCodes,
      tourGroupNames: this.selectedTourGroupNames,

      statusFilters: this.selectedStatusFilters,

      columnFilters: this.columnFilters,
      columnValueFilters: this.columnValueFilters,

      nightDay: this.scheduleIsNight,

      parcelEstimatedDeliveryDateStart: this.parcelEstimatedDeliveryDateStart,
      parcelEstimatedDeliveryDateEnd: this.parcelEstimatedDeliveryDateEnd,

      shuttleArrivalDateStart: this.shuttleArrivalDateStart,
      shuttleArrivalDateEnd: this.shuttleArrivalDateEnd,

      waybillDDT: this.searchDDTCodes,

      inRangeStatus: this.inRangeStatus,
      notificationStatus: this.notificationStatus,
      deliveryStatus: this.deliveryStatus,
      modifyStatus: this.modifyStatus,
      onTimeStatus: this.onTimeStatus,
      scheduleType: this.scheduleType,
      deliveryMethod: this.selectedDeliveryMethod,
      withProblemsStatus: this.withProblemsStatus,
      detailStatuses: this.selectedParcelDetailStatusValues || [],

      ddtReceivedStatus: this.ddtReceivedStatus,
      ddtPrintedStatus: this.ddtPrintedStatus,

      page: page,
      pageSize: pageSize,

      sortBy: this.sortState.active,
      sortDirection: this.sortState.direction
    };
  }

  loadData(page: number = 1): void {

  this.isLoading = true;

  this.currentPage = page;

  const body = this.buildSearchBody(this.currentPage, this.itemsPerPage);

  this.http.post<any>(
    `${environment.apiUrl}/overview-warehouse/parcels`,
    body
  ).subscribe({

    next: (response) => {

      this.totalRecords = response.totalCount;
      this.currentPage = response.page;
      this.itemsPerPage = response.pageSize;

      this.summaryReport = response.summary ?? {
        totalParcels: response.parcels?.length ?? 0,
        totalWeight: 0,
        totalVolume: 0,
        totalWaybills: response.totalCount ?? 0
      };

      this.parcels = response.parcels.map((p: ViewParcels) => {

        const isBigOrder =
          p.parcelWaybillDeliveryMethod === 'BIG_ORDER';

        return {
          ...p,
          isBigOrder,
          showTrackings: false,
          showComParcels: false,
          activeTab: 'tracking' as 'tracking' | 'map',
          minutesDelay: this.getMinutesDelayNumber(
            p.parcelOnTime,
            p.parcelDeliveryDate,
            p.parcelEstimatedDeliveryDate
          )
        };

      });

      this.filteredParcels = this.parcels;
      this.temp = [...this.filteredParcels];

      this.serverWaybillTotals.clear();
      const serverWaybills: any[] = response.waybills || [];
      for (const w of serverWaybills) {
        if (w?.waybillCode) {
          this.serverWaybillTotals.set(w.waybillCode, {
            totalWeight: Number(w.totalWeight) || 0,
            totalVolume: Number(w.totalVolume) || 0,
            parcelsCount: Number(w.parcelsCount) || 0
          });
        }
      }

      this.rebuildWaybillGroups();
      this.isLoading = false;
    },

    error: (err) => {
      console.error('Errore durante la chiamata HTTP:', err);
      this.isLoading = false;
    }

  });
}

loadSummary(): void {
  if (!this.warehouseCode) {
    this.summaryReport = { totalParcels: 0, totalWeight: 0, totalVolume: 0, totalWaybills: 0 };
    return;
  }

  this.isLoadingSummaryReport = true;
  const body = this.buildSearchBody(1, this.itemsPerPage);

  this.http.post<OverviewWarehouseSummary>(
    `${environment.apiUrl}/overview-warehouse/parcels/summary`,
    body
  ).subscribe({
    next: (summary) => {
      this.summaryReport = summary || { totalParcels: this.totalRecords, totalWeight: 0, totalVolume: 0 };
      this.isLoadingSummaryReport = false;
    },
    error: (err) => {
      console.error('Errore durante il caricamento del resoconto:', err);
      this.summaryReport = { totalParcels: this.totalRecords, totalWeight: 0, totalVolume: 0, totalWaybills: 0 };
      this.isLoadingSummaryReport = false;
    }
  });
}

toggleReportCollapsed(): void {
  this.isReportCollapsed = !this.isReportCollapsed;
}

  onGlobalSearchChange() {
    const term = this.globalSearchTerm.toLowerCase();

    if (!term) {
      this.filteredParcels = [...this.temp];
      this.rebuildWaybillGroups();
      return;
    }

    this.filteredParcels = this.temp.filter(parcel => {
      return Object.values(parcel).some(val =>
        val != null && val.toString().toLowerCase().includes(term)
      );
    });
    this.rebuildWaybillGroups();
  }

  toDateOnlyLocal(d: string | Date | null | undefined): string | null {
    if (!d) return null;

    const date = new Date(d);
    if (isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  formatDateForInput(date: string | Date | null): string | null {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    const hours = ('0' + d.getHours()).slice(-2);
    const minutes = ('0' + d.getMinutes()).slice(-2);
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }


  exportParcelsToExcel(): void {
    if (this.totalRecords === 0 || this.isExportingExcel) {
      return;
    }

    this.isExportingExcel = true;
    const body = this.buildSearchBody(1, Math.max(this.totalRecords, this.itemsPerPage));

    this.http.post<any>(`${environment.apiUrl}/overview-warehouse/parcels`, body).subscribe({
      next: (response) => {
        const allParcels: ViewParcels[] = response?.parcels ?? [];
        this.buildAndDownloadExcel(allParcels);
        this.isExportingExcel = false;
      },
      error: (err) => {
        this.isExportingExcel = false;
        console.error('Errore durante l\'esportazione Excel:', err);
        this.notificationService.error('Errore durante l\'esportazione Excel');
      }
    });
  }

  private buildAndDownloadExcel(parcels: ViewParcels[]): void {
    const exportData = parcels.map(p => ({
      'Codice Bolla': p.parcelOriginalWaybillCode || p.parcelWaybillCode,
      'Codice Nuova Bolla': (p.parcelOriginalWaybillCode && p.parcelOriginalWaybillCode !== p.parcelWaybillCode) ? p.parcelWaybillCode : '',
      'Codice Collo': p.parcelCode,
      'Cliente': p.clientName,
      'Stato': this.getTransitStatusLabel(p.parcelTransitStatus),
      'Data Affido': this.formatDate(p.parcelDate),
      'Data Picking Ingresso': p.parcelPickingEventStart ? this.formatDate(p.parcelPickingEventStart) : '',
      'Data Arrivo Navetta': p.parcelShuttleArrivalDate ? this.formatDate(p.parcelShuttleArrivalDate) : '',
      'Data di Stima Consegna': p.parcelEstimatedDeliveryDate ? this.formatDate(p.parcelEstimatedDeliveryDate) : '',
      'Data di Consegna': this.getConsegnaDateValue(p) ? this.formatDate(this.getConsegnaDateValue(p)!) : '',
      'Destinatario': p.dealerName,
      'Indirizzo Destinatario': p.dealerOperatingAddressStreet,
      'Città Destinatario': p.dealerOperatingAddressCity,
      'Regione': p.parcelRegionName,
      'Peso (kg)': p.parcelWeight,
      'Volume (m3)': p.parcelVolume,
      'Bolle Ricevute': p.waybillDdtSentDate ? this.formatDate(p.waybillDdtSentDate) : '',
      'Bolle Scaricate': p.waybillDownloadDdtDate ? this.formatDate(p.waybillDownloadDdtDate) : '',
      'Tour': p.parcelTourGroupName || '',
      'Descrizione Collo':
        p.parcelPalletType && p.parcelDescription
          ? `${p.parcelDescription} ${p.parcelPalletType}`
          : (p.parcelPalletType || p.parcelDescription || '')
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blobData = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

    FileSaver.saveAs(blobData, `totale_colli_${timestamp}.xlsx`);
  }

  exportParcelsOriginalToExcel(): void {
    // Seleziona solo i campi desiderati (escludendo trackings)
    const exportData = this.filteredParcels.map(p => ({
      ...(this.filteredClients.length > 1 && { 'Cliente': p.clientName }),
      ...(this.filteredBrands.length > 1 && { 'Marchio': p.brandName }),
      'Codice Collo': p.parcelCode,
      'Descrizione': p.parcelDescription || '',
      ...(this.canSpecial() && {'Tipologia': p.parcelDeliveryType}),
          ...(this.canSpecial()
      ? {
          'Codice Nuova Bolla': p.parcelOriginalWaybillCode ? p.parcelWaybillCode : '',
          'Codice Bolla': p.parcelOriginalWaybillCode ?? p.parcelWaybillCode
        }
      : {
          'Codice Bolla': p.parcelWaybillCode
        }),
      'Codici DDT': p.waybillDdtAttachments,
      ...(this.canSpecial() && {'Vettore': p.parcelWaybillCarrierName}),
      ...(this.canSpecial() && {'magazzino': this.getWarehouseName(p.parcelWaybillLastMileOrigin)}),
      'Codice Destinatario': p.parcelDealerCode,
      'Destinatario': p.dealerName,
      'Indirizzo destinatario': p.dealerOperatingAddressStreet,
      'Città destinatario': p.dealerOperatingAddressCity,
      'Regione': p.parcelRegionName,
      'Data Affido': this.formatDate(p.parcelDate),
      'Data Consegna': this.formatDate(p.parcelDeliveryDate),
      ...(this.canSpecial() && {'Stima Consegna': this.formatDate(p.parcelEstimatedDeliveryDate)}),
      ...(this.canSpecial() && {'In Orario': this.descrOnTime(p.parcelOnTime)}),
      ...(this.canSpecial() && {'Ritardo (min)': p.minutesDelay == 0 ? '' : p.minutesDelay}),
      ...(this.canSpecial() && {'Tipologia Consegna': this.descrDeliveryMethod(p.parcelWaybillDeliveryMethod)}),
      ...(this.canSpecial() && {'Tour': p.parcelWaybillDeliveryType}),
      ...(this.canSpecial() && {'Baia': p.parcelWaybillLoadingBay}),
      ...(this.canSpecial() && {'Stato': p.parcelDeliveryStatus === 'DELIVERED' ? 'Consegnato' : p.parcelDeliveryStatus}),
      ...(this.canSpecial() && {'Causale Consegna': p.parcelDeliveryReason}),
      ...(this.canSpecial() && {'Lat': p.parcelLatitude != null ? p.parcelLatitude.toString() : ''}),
      ...(this.canSpecial() && {'Lng': p.parcelLongitude != null ? p.parcelLongitude.toString() : ''}),
      ...(this.canSpecial() && {'Distanza (m)': p.distanceInMeters ? Math.round(p.distanceInMeters) : ''}),
      'Peso': p.parcelWeight,
      'Volume': p.parcelVolume,
      'Descrizione Collo':
        (p.parcelDeliveryType ? p.parcelDeliveryType : '') +
        (p.parcelDeliveryType && p.parcelDescription ? ' ' : '') +
        (p.parcelDescription ? p.parcelDescription : ''),
      ...(this.canSpecial() && {'Misrouting': this.descrY(p.parcelHasMisrouting)}),
      ...(this.canSpecial() && {'Modificato': this.formatDate(p.parcelModifyManual)}),
      // dati originali
      ...(this.canSpecial() && {'Data Consegna Originale PTV': this.formatDate(p.parcelOriginalDeliveryDate)}),
      ...(this.canSpecial() && {'In Orario Originale': this.getMinutesDelayNumber('',p.parcelOriginalDeliveryDate,p.parcelEstimatedDeliveryDate) <= 0 ? 'Si' : 'No'}),
      ...(this.canSpecial() && {'Ritardo (min) Originale': this.getMinutesDelayNumber('',p.parcelOriginalDeliveryDate,p.parcelEstimatedDeliveryDate)}),
      ...(this.canSpecial() && {'Data Modificata': this.descrY(p.parcelDeliveryDateIsUpdated)}),
      ...(this.canSpecial() && {'Latitudine Originale PTV':  p.parcelOriginalLatitude != null ? p.parcelOriginalLatitude.toString() : ''}),
      ...(this.canSpecial() && {'Longitudine Originale PTV': p.parcelOriginalLongitude != null ? p.parcelOriginalLongitude.toString() : ''}),
      ...(this.canSpecial() && {'Coordinate Modificate': this.coordinatesHasModified(p)}),
      ...(this.canSpecial() && {'Distanza (m) Originale PTV': p.distanceOriginalInMeters ? Math.round(p.distanceOriginalInMeters) : ''})
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const blobData = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const now = new Date();
    const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

    FileSaver.saveAs(blobData, `totale_colli_con_dati_originali_${timestamp}.xlsx`);
  }

  coordinatesHasModified(p: ViewParcels): string {
    if (p.parcelDeliveryCoordinatesIsUpdated !== 'Y') {
      return '';
    }

    const latChanged = p.parcelLatitude !== p.parcelOriginalLatitude;
    const lonChanged = p.parcelLongitude !== p.parcelOriginalLongitude;

    if(latChanged || lonChanged)
      return 'Si';

    return '';
  }

  private descrY(parcelModified: string): string {
    switch (parcelModified) {
      case 'Y': return 'Si';
      case 'N': return 'No';
      default: return parcelModified;
    }
  }

  private descrOnTime(parcelOnTime: string): string {
    switch (parcelOnTime) {
      case 'ON_TIME': return 'Si';
      case 'NOT_ON_TIME': return 'No';
      default: return parcelOnTime;
    }
  }

  private descrDeliveryMethod(parcelWaybillDeliveryMethod: string): string {
    switch (parcelWaybillDeliveryMethod) {
      case 'BY_ROAD': return 'Via terra';
      case 'BY_AIR': return 'Via aerea';
      default: return parcelWaybillDeliveryMethod;
    }
  }

  private formatFilterDay(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  private compareFilterOptions(a: string, b: string): number {
    const re = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const ma = re.exec(a);
    const mb = re.exec(b);
    if (ma && mb) {
      return `${ma[3]}${ma[2]}${ma[1]}`.localeCompare(`${mb[3]}${mb[2]}${mb[1]}`);
    }
    return a.localeCompare(b);
  }

  // Formatta date in formato dd/MM/yyyy HH:mm
  private formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }


  resetFilters(): void {
    this.scheduleType = '';
    this.selectedClients = [];
    this.selectedBrands = [];
    this.searchDealerCode = '';
    this.searchDDTCodes = '';
    this.selectedDealerCode = '';
    this.notificationStatus = '';
    this.justificationStatus = '';
    this.deliveryStatus = '';
    this.ddtReceivedStatus = '';
    this.ddtPrintedStatus = '';
    this.searchParcelCode = '';
    this.searchParcelWaybillCode = '';
    this.inRangeStatus = '';
    this.selectedCarrierCode = '';
    this.selectedRegionCode = '';
    this.onTimeStatus = '';
    this.modifyStatus = '';
    this.withProblemsStatus = '';
    this.selectedDeliveryType = '';
    this.scheduleIsNight = '';
    this.selectedDeliveryMethod = '';
    this.parcelDateStart = '';
    this.parcelDateEnd = '';
    this.parcelEstimatedDeliveryDateStart = '';
    this.parcelEstimatedDeliveryDateEnd = '';
    this.shuttleArrivalDateStart = '';
    this.shuttleArrivalDateEnd = '';
    this.misroutingStatus = '';
    this.showUndefinedParcels = false;
    this.showBigOrders = false;

    this.selectedCarrierCodes = [];
    this.selectedWarehouseFromCodes = [];
    this.selectedRegionCodes = [];
    this.selectedTourGroupNames = [];
    this.selectedParcelDetailStatusValues = [];
    this.columnFilters = {};
    this.columnValueFilters = {};
    this.openColumnFilterKey = null;

    if (this.filteredWarehouses.length !== 1) {
      this.warehouseCode = '';
    }
    this.autoSelectSingleWarehouse();

    this.datePickers.forEach(picker => picker.clear());
    this.clearDateError();
    this.clearSelection();

    this.applyFilters();
  }



  calcolaDataDefaultFiltri(): void {
    // Calcolo data predefinita (ieri o venerdì)
    const today = new Date();
    let defaultDate = new Date(today);

    if (today.getDay() === 0) {
      // Domenica → venerdì
      defaultDate.setDate(today.getDate() - 2);
    } else if (today.getDay() === 1) {
      // Lunedì → venerdì
      defaultDate.setDate(today.getDate() - 3);
    } else {
      // Altri giorni → ieri
      defaultDate.setDate(today.getDate() - 1);
    }

    const formattedDate = defaultDate.toISOString().split('T')[0];
    this.parcelDateStart = formattedDate;
    this.parcelDateEnd = formattedDate;
  }

  viewDetailsModifiesManual(parcel: ViewParcels) {
    this.selectedParcel = parcel;
    this.showModalDetailModifies = true;
  }

  closeModalDetailModifies() {
    this.selectedParcel = null;
    this.showModalDetailModifies = false;
  }

  private dateInputFor(field: OverviewDateField): HTMLInputElement | undefined {
    switch (field) {
      case 'parcelDateStart': return this.parcelDateStartInput?.nativeElement;
      case 'parcelDateEnd': return this.parcelDateEndInput?.nativeElement;
      case 'parcelEstimatedDeliveryDateStart': return this.parcelEstimatedDeliveryDateStartInput?.nativeElement;
      case 'parcelEstimatedDeliveryDateEnd': return this.parcelEstimatedDeliveryDateEndInput?.nativeElement;
      case 'shuttleArrivalDateStart': return this.shuttleArrivalDateStartInput?.nativeElement;
      case 'shuttleArrivalDateEnd': return this.shuttleArrivalDateEndInput?.nativeElement;
    }
  }

  getFormattedDate(field: OverviewDateField): string {
    const value = this[field];
    if (!value) {
      return '';
    }
    const [year, month, day] = value.split('-');
    return day && month && year ? `${day}/${month}/${year}` : value;
  }

  openDatePicker(field: OverviewDateField): void {
    let picker = this.datePickers.get(field);

    if (!picker) {
      const input = this.dateInputFor(field);
      if (!input) {
        return;
      }
      picker = flatpickr(input, {
        dateFormat: 'd/m/Y',
        allowInput: false,
        clickOpens: false,
        locale: Italian,
        defaultDate: this[field] || undefined,
        onChange: (selectedDates: Date[]) => {
          this[field] = selectedDates.length ? this.toIsoDay(selectedDates[0]) : '';
          this.clearDateError();
          this.datePickers.get(field)?.close();
        },
        onClose: () => {
          this.dateInputFor(field)?.blur();
        }
      });
      this.datePickers.set(field, picker);
    }

    if (!picker.isOpen) {
      if (this[field]) {
        picker.setDate(this[field], false);
      }
      picker.open();
    }
  }

  shiftDateField(field: OverviewDateField, days: number): void {
    const base = this[field] ? new Date(this[field]) : new Date();
    if (this[field]) {
      base.setDate(base.getDate() + days);
    }
    this.setDateField(field, this.toIsoDay(base));
  }

  setDateFieldToday(field: OverviewDateField): void {
    this.setDateField(field, this.toIsoDay(new Date()));
  }

  clearDateField(field: OverviewDateField): void {
    this[field] = '';
    this.datePickers.get(field)?.clear();
    this.clearDateError();
  }

  private setDateField(field: OverviewDateField, value: string): void {
    this[field] = value;
    this.datePickers.get(field)?.setDate(value, false);
    this.clearDateError();
  }

  private toIsoDay(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private setDateError(message: string, scope: 'affido' | 'stimata' | 'entrambi'): void {
    this.dateError = message;
    this.dateErrorScope = scope;
  }

  private clearDateError(): void {
    this.dateError = '';
    this.dateErrorScope = null;
  }

  isDateFieldInError(field: OverviewDateField): boolean {
    if (!this.dateErrorScope) {
      return false;
    }
    const isAffido = field === 'parcelDateStart' || field === 'parcelDateEnd';
    return this.dateErrorScope === 'entrambi'
      || (this.dateErrorScope === 'affido' && isAffido)
      || (this.dateErrorScope === 'stimata' && !isAffido);
  }


  applySortManual(): void {
    if (this.sortState.active && this.sortState.direction) {
      const isAsc = this.sortState.direction === 'asc';
      this.filteredParcels = [...this.filteredParcels].sort((a, b) =>
        this.compare(
          (a as any)[this.sortState.active] || '',
          (b as any)[this.sortState.active] || '',
          isAsc
        )
      );
    } else {
      // Ripristina l'ordine originale
      //this.filteredParcels = [...this.parcels]; // o una copia della lista originale
    }
  }


  compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  getMinutesDelayNumber(parcelOnTime: string, parcelDeliveryDate: string, parcelEstimatedDeliveryDate: string): number {
  if (parcelOnTime === 'ON_TIME' || !parcelDeliveryDate || !parcelEstimatedDeliveryDate) {
    return 0;
  }

  const deliveryDate = new Date(parcelDeliveryDate);
  const estimatedDate = new Date(parcelEstimatedDeliveryDate);

  const diffMs = deliveryDate.getTime() - estimatedDate.getTime();
  return Math.max(Math.floor(diffMs / (1000 * 60)), 0);
}

  getValue(p: any, field: string): any {
    return p[field];
  }

  undoParcel(parcel: any, allParcels : boolean) {

      this.isLoading = true;
      this.showRollbackModal = true;

      this.http.put(`${environment.apiUrl}/tracking/rollbackDelivery/${parcel.parcelCode}/${allParcels}`, null).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.loadData();
        this.closeRollbackModal();
        this.notificationService.success('Consegna annullata con successo!');
      },
      error: (err) => {
        this.isLoading = false;
        this.closeRollbackModal();
        const message = this.errorHelperService.extractBackendMessage(err);
        this.notificationService.error(`Errore: ${message}`);
      }
    });
    }

    closeRollbackModal() {
    this.showRollbackModal = false;
    this.selectedParcel = null;
    this.rollbackChoice = null;
  }

    openRollbackModal(parcel: any) {
    this.selectedParcel = parcel;
    this.showRollbackModal = true;
    this.rollbackChoice = null;
    this.numberParcelsOfWaybill = this.parcels.filter(p => p.parcelWaybillCode == parcel.parcelWaybillCode && p.parcelDeliveryStatus == "DELIVERED").length;
  }

  startRollback(scope: 'parcel' | 'waybill') {
    this.rollbackChoice = scope;
  }

  cancelRollback() {
    this.rollbackChoice = null;
  }

  confirmRollback() {
    if (!this.selectedParcel || !this.rollbackChoice) return;
    const allParcels = this.rollbackChoice === 'waybill';
    this.undoParcel(this.selectedParcel, allParcels);
  }

  coordinateRegex = /^-?[1-9]\d?([,.]\d+)?$/;

  coordinatesValid = true;

  checkCoordinates() {
    const latStr = this.editForm.parcelLatitude?.toString().replace(',', '.').trim();
    const lonStr = this.editForm.parcelLongitude?.toString().replace(',', '.').trim();

    const latEmpty = !latStr;
    const lonEmpty = !lonStr;

    const latOk = latEmpty || this.coordinateRegex.test(latStr);
    const lonOk = lonEmpty || this.coordinateRegex.test(lonStr);

    this.coordinatesValid = latOk && lonOk;

    if (latOk && !latEmpty) {
      this.editForm.parcelLatitude = parseFloat(latStr);
    }
    if (lonOk && !lonEmpty) {
      this.editForm.parcelLongitude = parseFloat(lonStr);
    }
  }

  getJustificationLabel(type: string): string {
    switch (type) {
      case 'MISSING_DELIVERY': return 'MANCATA CONSEGNA';
      case 'DELAY': return 'RITARDO';
      default: return type;
    }
  }

  getLineClassStart(p: ViewParcels) {
    if (p.parcelDate) return 'line-green';
    return 'line-grey';
  }

  getWarehouseName(code: string) {
    return this.filteredWarehouses.filter(w => w.warehouseCode == code).length > 0 ? this.filteredWarehouses.filter(w => w.warehouseCode == code)[0].warehouseName : code;
  }

  getWarehouseNameComParcel(code: string | undefined) {
    return this.filteredWarehouses.filter(w => w.warehouseCode == code).length > 0 ? this.filteredWarehouses.filter(w => w.warehouseCode == code)[0].warehouseName : null;
  }

  getWarehouseShortNameComParcel(code: string | undefined) {
    return this.filteredWarehouses.filter(w => w.warehouseCode == code).length > 0 ? this.filteredWarehouses.filter(w => w.warehouseCode == code)[0].warehouseShortName : null;
  }

  getDealerName(code: string | undefined) {
    return this.filteredDealers.filter(d => d.dealerCode == code).length > 0 ? this.filteredDealers.filter(d => d.dealerCode == code)[0].dealerName : null;
  }

  getCarrierName(code: string | undefined) { 
    return this.filteredCarriers.filter(d => d.carrierCode == code).length > 0 ? this.filteredCarriers.filter(d => d.carrierCode == code)[0].carrierName : code;
  }


  toDate(d?: string | Date | null): Date | null {
    if (!d) return null;
    return d instanceof Date ? d : new Date(d);
  }


  private matchesOnTimeFilter(parcel: ViewParcels): boolean {
    // Nessun filtro selezionato → sempre true
    if (!this.onTimeStatus) {
      return true;
    }

    const status = parcel.parcelOnTime;

    // Filtro base solo sullo stato ON_TIME / NOT_ON_TIME
    if (this.onTimeStatus === 'ON_TIME') {
      return status === 'ON_TIME';
    }

    if (this.onTimeStatus === 'NOT_ON_TIME') {
      return status === 'NOT_ON_TIME';
    }

    // Da qui in poi filtriamo sui bucket di ritardo, SOLO se NOT_ON_TIME
    if (status !== 'NOT_ON_TIME') {
      return false;
    }

    // calcolo differenza SOLO se NOT_ON_TIME
    const delayMinutes = this.getMinutesDelayNumber(
      parcel.parcelOnTime,
      parcel.parcelDeliveryDate,
      parcel.parcelEstimatedDeliveryDate
    );

    // se non riesco a calcolare o è zero, non rientra nei bucket
    if (!delayMinutes || delayMinutes <= 0) {
      return false;
    }

    switch (this.onTimeStatus) {
      case 'DELAY_LT_2H':
        // ritardo > 0 e ≤ 2h
        return delayMinutes > 0 && delayMinutes <= 120;

      case 'DELAY_2H_3H':
        // 2h < ritardo ≤ 3h
        return delayMinutes > 120 && delayMinutes <= 180;

      case 'DELAY_3H_1D':
        // 3h < ritardo ≤ 1 giorno (1440 min)
        return delayMinutes > 180 && delayMinutes <= 1440;

      case 'DELAY_GT_1D':
        // ritardo > 1 giorno
        return delayMinutes > 1440;

      default:
        return true;
    }
  }

  getParcelDetailStatusLabel(status: string | null | undefined): string {
    if (!status) return '';

    switch (status) {
      case 'PREPARED':
        return ParcelDetailStatus.PREPARED;
      case 'CLOSED':
        return ParcelDetailStatus.CLOSED;
      case 'IN_TRIP_TO_TP':
        return ParcelDetailStatus.IN_TRIP_TO_TP;
      case 'IN_TRIP':
        return ParcelDetailStatus.IN_TRIP;
      case 'SUSPENDED':
        return ParcelDetailStatus.SUSPENDED;
      default:
        // se ti arriva già "Preparata" o qualcosa di imprevisto
        return status;
    }
  }

  openImportExcelModal() {
    this.showImportExcelModal = true;
  }

  closeImportExcelModal() {
    this.showImportExcelModal = false;
    this.selectedExcelFile = null!;
  }

  onFileSelected(event: any) {
    if (event.target.files.length > 0) {
      this.selectedExcelFile = event.target.files[0];
    }
  }

downloadTemplateExcel() {

  this.http.get(`${environment.apiUrl}/overview-harmonizer/template-deliveries`, {
    responseType: 'blob'
  }).subscribe(res => {

    const blob = new Blob([res], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_consegne.xlsx';
    a.click();

    window.URL.revokeObjectURL(url);
  });
}

  importExcelDeliveries() {

    this.isLoading = true;
    const formData = new FormData();
    formData.append('file', this.selectedExcelFile);

    this.http.post(`${environment.apiUrl}/overview-harmonizer/import-excel-deliveries`, formData)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.loadData();
          this.closeImportExcelModal();
          this.notificationService.success('Excel importato con successo');
        },
        error: err => {
          this.isLoading = false;
          this.closeImportExcelModal();
          const message = this.errorHelperService.extractBackendMessage(err);
          this.notificationService.error(`Errore: ${message}`);
        }
      });
  }

  isStatusFilterActive(status: PlatformTransitStatus): boolean {
    return this.selectedStatusFilters.includes(status);
  }

  toggleStatusFilter(status: PlatformTransitStatus): void {
    if (this.selectedStatusFilters.includes(status)) {
      if (this.selectedStatusFilters.length === 1) {
        return;
      }
      this.selectedStatusFilters = this.selectedStatusFilters.filter(s => s !== status);
    } else {
      this.selectedStatusFilters = [...this.selectedStatusFilters, status];
    }
    this.applyFilters();
  }

  getTransitStatusLabel(status: string | undefined): string {
    switch (status) {
      case 'ARRIVING': return 'In Arrivo';
      case 'ARRIVED': return 'Arrivato';
      case 'CLOSED': return 'Uscito';
      case 'DELIVERED': return 'Consegnato';
      case 'DELIVERED_NO_OUTCOME': return 'Consegnato non esitato';
      default: return '-';
    }
  }

  getTransitStatusBadgeClass(status: string | undefined): string {
    switch (status) {
      case 'ARRIVING': return 'transit-badge transit-badge--arriving';
      case 'ARRIVED': return 'transit-badge transit-badge--arrived';
      case 'CLOSED': return 'transit-badge transit-badge--closed';
      case 'DELIVERED': return 'transit-badge transit-badge--delivered';
      case 'DELIVERED_NO_OUTCOME': return 'transit-badge transit-badge--delivered-no-outcome';
      default: return 'transit-badge transit-badge--unknown';
    }
  }

  columnValueFilters: { [key: string]: string[] } = {};
  columnDistinctValues: string[] = [];
  columnDistinctValuesLoading: boolean = false;
  private columnValueDraftSelected = new Set<string>();
  columnValueSearchTerm: string = '';

  private readonly parcelColumnAccessors: { [key: string]: (p: ViewParcels) => string | undefined } = {
    parcelOriginalWaybillCode: p => p.parcelOriginalWaybillCode || p.parcelWaybillCode,
    parcelWaybillCode: p => p.parcelWaybillCode,
    clientName: p => p.clientName,
    dealerName: p => p.dealerName,
    dealerOperatingAddressStreet: p => p.dealerOperatingAddressStreet,
    dealerOperatingAddressCity: p => p.dealerOperatingAddressCity,
    parcelRegionName: p => p.parcelRegionName,
    parcelTourGroupName: p => p.parcelTourGroupName,
    parcelDate: p => p.parcelDate ? this.formatFilterDay(p.parcelDate) : undefined,
    parcelConsegnaDate: p => this.getConsegnaDateValue(p) ? this.formatFilterDay(this.getConsegnaDateValue(p)!) : undefined,
    parcelEstimatedDeliveryDate: p => p.parcelEstimatedDeliveryDate ? this.formatFilterDay(p.parcelEstimatedDeliveryDate) : undefined,
    parcelPickingEventStart: p => p.parcelPickingEventStart ? this.formatFilterDay(p.parcelPickingEventStart) : undefined,
    parcelShuttleArrivalDate: p => p.parcelShuttleArrivalDate ? this.formatFilterDay(p.parcelShuttleArrivalDate) : undefined
  };

  // La "Data di Consegna" della overview è sempre e solo la data di consegna originale (PARCEL_ORIGINAL_DELIVERY_DATE)
  getConsegnaDateValue(p: { parcelOriginalDeliveryDate?: string }): string | undefined {
    return p.parcelOriginalDeliveryDate || undefined;
  }

  columnFilterPopoverStyle: { top: string, left: string } = { top: '0px', left: '0px' };

  toggleColumnFilter(column: string, event: Event): void {
    event.stopPropagation();
    if (this.openColumnFilterKey === column) {
      this.openColumnFilterKey = null;
      return;
    }
    this.openColumnFilterKey = column;
    this.columnFilterDraft = this.columnFilters[column] || '';
    this.columnValueSearchTerm = '';
    this.columnValueDraftSelected = new Set(this.columnValueFilters[column] || []);

    const icon = event.currentTarget as HTMLElement;
    const rect = icon.getBoundingClientRect();
    const popoverWidth = 280;
    const margin = 8;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - margin) {
      left = window.innerWidth - popoverWidth - margin;
    }
    left = Math.max(margin, left);
    this.columnFilterPopoverStyle = {
      top: (rect.bottom + 6) + 'px',
      left: left + 'px'
    };

    this.loadColumnDistinctValues(column);
  }

  isColumnFilterOpen(column: string): boolean {
    return this.openColumnFilterKey === column;
  }

  isColumnFilterActive(column: string): boolean {
    return !!this.columnFilters[column] || (this.columnValueFilters[column]?.length > 0);
  }

  applyColumnFilter(column: string): void {
    if (this.columnFilterDraft && this.columnFilterDraft.trim()) {
      this.columnFilters = { ...this.columnFilters, [column]: this.columnFilterDraft.trim() };
    } else {
      const { [column]: _removed, ...rest } = this.columnFilters;
      this.columnFilters = rest;
    }
    this.openColumnFilterKey = null;
    this.applyFilters();
  }

  clearColumnFilter(column: string, event?: Event): void {
    event?.stopPropagation();
    const { [column]: _removedText, ...restText } = this.columnFilters;
    this.columnFilters = restText;
    const { [column]: _removedValues, ...restValues } = this.columnValueFilters;
    this.columnValueFilters = restValues;
    this.columnValueDraftSelected = new Set();
    this.openColumnFilterKey = null;
    this.applyFilters();
  }

  @HostListener('document:click')
  closeOpenColumnFilterOnOutsideClick(): void {
    this.openColumnFilterKey = null;
  }

  @HostListener('window:scroll')
  closeOpenColumnFilterOnScroll(): void {
    this.openColumnFilterKey = null;
  }

  loadColumnDistinctValues(column: string): void {
    const accessor = this.parcelColumnAccessors[column];
    if (!accessor || !this.warehouseCode) {
      this.columnDistinctValues = [];
      return;
    }

    this.columnDistinctValuesLoading = true;

    const savedText = this.columnFilters[column];
    const savedValues = this.columnValueFilters[column];
    delete this.columnFilters[column];
    delete this.columnValueFilters[column];

    const body = this.buildSearchBody(1, Math.max(this.totalRecords || this.itemsPerPage, this.itemsPerPage));

    if (savedText !== undefined) { this.columnFilters[column] = savedText; }
    if (savedValues !== undefined) { this.columnValueFilters[column] = savedValues; }

    this.http.post<any>(`${environment.apiUrl}/overview-warehouse/parcels`, body).subscribe({
      next: (response) => {
        const parcels: ViewParcels[] = response?.parcels ?? [];
        const distinct = new Set<string>();
        parcels.forEach(p => {
          const value = accessor(p);
          if (value) { distinct.add(value); }
        });
        this.columnDistinctValues = Array.from(distinct).sort((a, b) => this.compareFilterOptions(a, b));
        this.columnDistinctValuesLoading = false;
      },
      error: (err) => {
        console.error('Errore nel caricamento dei valori distinti per la colonna:', err);
        this.columnDistinctValues = [];
        this.columnDistinctValuesLoading = false;
      }
    });
  }

  getFilteredColumnDistinctValues(): string[] {
    const term = this.columnValueSearchTerm.trim().toLowerCase();
    if (!term) {
      return this.columnDistinctValues;
    }
    return this.columnDistinctValues.filter(v => v.toLowerCase().includes(term));
  }

  isColumnValueChecked(value: string): boolean {
    return this.columnValueDraftSelected.has(value);
  }

  toggleColumnValue(value: string): void {
    if (this.columnValueDraftSelected.has(value)) {
      this.columnValueDraftSelected.delete(value);
    } else {
      this.columnValueDraftSelected.add(value);
    }
  }

  areAllVisibleColumnValuesSelected(): boolean {
    const visible = this.getFilteredColumnDistinctValues();
    return visible.length > 0 && visible.every(v => this.columnValueDraftSelected.has(v));
  }

  toggleSelectAllColumnValues(): void {
    const visible = this.getFilteredColumnDistinctValues();
    if (this.areAllVisibleColumnValuesSelected()) {
      visible.forEach(v => this.columnValueDraftSelected.delete(v));
    } else {
      visible.forEach(v => this.columnValueDraftSelected.add(v));
    }
  }

  applyColumnValueFilter(column: string): void {
    const selected = Array.from(this.columnValueDraftSelected);
    if (selected.length > 0) {
      this.columnValueFilters = { ...this.columnValueFilters, [column]: selected };
    } else {
      const { [column]: _removed, ...rest } = this.columnValueFilters;
      this.columnValueFilters = rest;
    }
    this.openColumnFilterKey = null;
    this.applyFilters();
  }

  openDdtSentModal(group: WaybillGroup, event?: Event): void {
    event?.stopPropagation();
    if (!group.waybillDdtSentDate) {
      return;
    }
    this.selectedWaybillGroupForModal = group;
    this.showDdtSentModal = true;
  }

  closeDdtSentModal(): void {
    this.showDdtSentModal = false;
    this.selectedWaybillGroupForModal = null;
  }

  openDdtDownloadModal(group: WaybillGroup, event?: Event): void {
    event?.stopPropagation();
    if (!group.waybillDownloadDdtDate) {
      return;
    }
    this.selectedWaybillGroupForModal = group;
    this.showDdtDownloadModal = true;
  }

  closeDdtDownloadModal(): void {
    this.showDdtDownloadModal = false;
    this.selectedWaybillGroupForModal = null;
  }

  formatDdtDate(date: any): string {
    return this.formatDate(date);
  }

  private printWaybillsPdf(waybillsMap: { key: string, value: string }[]): void {
    if (!waybillsMap.length) {
      return;
    }

    this.isPrintingPDF = true;
    this.http.post(
      `${environment.apiUrl}/overview-warehouse/waybills-print`,
      waybillsMap,
      { responseType: 'blob', observe: 'response' }
    ).subscribe({
      next: (response) => {
        this.isPrintingPDF = false;

        const blob = response.body as Blob;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
        FileSaver.saveAs(blob, `Multiple_Waybills_${timestamp}.pdf`);

        const now = new Date().toISOString();
        const printedCodes = new Set(waybillsMap.map(w => w.key));
        this.groupedWaybills.forEach(g => {
          if (printedCodes.has(g.waybillCode)) {
            g.waybillDownloadDdtDate = now;
          }
        });

        this.notificationService.success(
          `PDF generato e scaricato con successo per ${waybillsMap.length} ${waybillsMap.length === 1 ? 'bolla' : 'bolle'}`
        );
      },
      error: (error) => {
        this.isPrintingPDF = false;
        console.error('Error generating PDF:', error);
        this.notificationService.error('Errore durante la generazione del PDF');
      }
    });
  }

  printSingleWaybillFromRow(group: WaybillGroup, event?: Event): void {
    event?.stopPropagation();
    this.printWaybillsPdf([{ key: group.waybillCode, value: group.waybillShipmentCode || '' }]);
  }

  private downloadEdiCsv(waybillsMap: { key: string, value: string }[]): void {
    if (!waybillsMap.length) {
      return;
    }

    this.isDownloadingEdi = true;
    this.http.post(
      `${environment.apiUrl}/overview-warehouse/waybills-edi-export`,
      waybillsMap,
      { responseType: 'blob', observe: 'response' }
    ).subscribe({
      next: (response) => {
        this.isDownloadingEdi = false;

        const blob = response.body as Blob;
        const timestamp = new Date().toISOString().replace(/[:.]/g, '').slice(0, 15);
        FileSaver.saveAs(blob, `EDI_FILE_PARCELS_${timestamp}.csv`);

        this.notificationService.success(
          `File EDI generato e scaricato con successo per ${waybillsMap.length} ${waybillsMap.length === 1 ? 'bolla' : 'bolle'}`
        );
      },
      error: (error) => {
        this.isDownloadingEdi = false;
        console.error('Error generating EDI file:', error);
        this.notificationService.error('Errore durante la generazione del file EDI');
      }
    });
  }

  downloadEdiCsvFromRow(group: WaybillGroup, event?: Event): void {
    event?.stopPropagation();
    this.downloadEdiCsv([{ key: group.waybillCode, value: group.waybillShipmentCode || '' }]);
  }

  downloadSelectedEdiCsv(): void {
    const selectedWaybillCodes = Array.from(this.selectedWaybills.keys());

    if (selectedWaybillCodes.length === 0) {
      this.notificationService.warning('Nessuna bolla selezionata per il file EDI');
      return;
    }

    const waybillsMap: { key: string, value: string }[] = [];
    this.selectedWaybills.forEach((shipmentCode, waybillCode) => {
      waybillsMap.push({ key: waybillCode, value: shipmentCode });
    });

    this.downloadEdiCsv(waybillsMap);
  }

setSort(field: string): void {

  if (this.sortState.active === field) {

    if (this.sortState.direction === 'asc') {
      this.sortState.direction = 'desc';

    } else if (this.sortState.direction === 'desc') {
      this.sortState.active = '';
      this.sortState.direction = '';

    } else {
      this.sortState.direction = 'asc';
    }

  } else {
    this.sortState.active = field;
    this.sortState.direction = 'asc';
  }

  this.loadData();
}

  rebuildSelectOptions(): void {
    this.clientSelectOptions = this.filteredClients.map(c => ({
      value: c.clientCode,
      label: c.clientName || c.clientCode
    }));
    this.brandSelectOptions = this.filteredBrands.map(b => ({
      value: b.brandCode,
      label: b.brandName || b.brandCode
    }));
    this.dealerSelectOptions = this.filteredDealers.map(d => ({
      value: d.dealerCode,
      label: d.dealerName || d.dealerCode
    })) as SearchableSelectOption[];
    this.carrierSelectOptions = this.filteredCarriers.map(c => ({
      value: c.carrierCode,
      label: c.carrierName || c.carrierCode
    }));
    this.warehouseFromSelectOptions = this.filteredWarehousesFrom.map(w => ({
      value: w.warehouseCode,
      label: w.warehouseName || w.warehouseCode
    }));
    this.warehouseSelectOptions = this.filteredWarehouses.map(w => ({
      value: w.warehouseCode,
      label: this.buildWarehouseOptionLabel(w)
    }));
    this.regionSelectOptions = this.filteredRegions.map(r => ({
      value: r.regionCode,
      label: r.regionName || r.regionCode
    }));
    this.tourGroupSelectOptions = this.tourGroupOptions.map(g => ({
      value: g.name,
      label: g.name
    }));
    this.parcelDetailStatusSelectOptions = this.parcelDetailStatusOptions.map(s => ({
      value: s.value,
      label: s.label
    }));
  }

  autoSelectSingleWarehouse(): void {
    if (this.filteredWarehouses.length === 1) {
      this.warehouseCode = this.filteredWarehouses[0].warehouseCode;
      this.warehouseError = false;
    }
    // Selezione non piu' presente fra le opzioni: va scartata
    if (this.warehouseCode
        && this.filteredWarehouses.length > 0
        && !this.filteredWarehouses.some(w => w.warehouseCode === this.warehouseCode)) {
      this.warehouseCode = '';
    }
  }

  get isSingleWarehouse(): boolean {
    return this.filteredWarehouses.length === 1;
  }

  get selectedWarehouseName(): string {
    const w = this.filteredWarehouses.find(x => x.warehouseCode === this.warehouseCode);
    return w ? this.buildWarehouseOptionLabel(w) : '';
  }

  private buildWarehouseOptionLabel(w: Warehouse): string {
    const name = w.warehouseName || w.warehouseCode;
    return w.warehouseShortName ? `${name} (${w.warehouseShortName})` : name;
  }

  onWarehouseChange(): void {
    this.warehouseError = !this.warehouseCode;
    if (this.warehouseCode && this.dateError.startsWith('Seleziona un TP')) {
      this.clearDateError();
    }
    this.selectedTourGroupNames = [];
    this.loadTourGroups();
    if(this.warehouseCode) {
      this.applyFilters();
    }
  }

  loadTourGroups(): void {
    if (!this.warehouseCode) {
      this.tourGroupOptions = [];
      this.tourGroupSelectOptions = [];
      return;
    }
    this.http.get<TourGroupOption[]>(
      `${environment.apiUrl}/overview-warehouse/tour-groups`,
      { params: { warehouseCode: this.warehouseCode } }
    ).subscribe({
      next: (groups) => {
        this.tourGroupOptions = groups || [];
        this.rebuildSelectOptions();
      },
      error: (err) => {
        console.error('Errore nel caricamento dei tour groups:', err);
        this.tourGroupOptions = [];
        this.rebuildSelectOptions();
      }
    });
  }

  hasActiveFilters(): boolean {
    return !!(this.searchParcelCode || this.searchParcelWaybillCode || this.searchDDTCodes
      || this.selectedClients.length > 0 || this.selectedBrands.length > 0
      || this.selectedWarehouseFromCodes.length > 0 || this.selectedRegionCodes.length > 0
      || this.selectedTourGroupNames.length > 0 || this.selectedParcelDetailStatusValues.length > 0
      || this.notificationStatus || this.deliveryStatus || this.modifyStatus
      || this.onTimeStatus || this.inRangeStatus || this.scheduleType
      || this.scheduleIsNight || this.selectedDeliveryMethod
      || this.parcelDateStart || this.parcelDateEnd
      || this.parcelEstimatedDeliveryDateStart || this.parcelEstimatedDeliveryDateEnd);
  }

  setItemsPerPage(size: number): void {
    if (!this.pageSizeOptions.includes(size)) {
      return;
    }
    this.itemsPerPage = size;
    this.onItemsPerPageChange();
    this.loadData();
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  getPageNumbers(): number[] {
    const totalPages = this.getTotalPages();
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  trackByPageNumber(index: number, page: number): number {
    return page;
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }

  isParcelSelected(p: ViewParcels): boolean {
    return !!p.parcelWaybillCode && this.selectedWaybills.has(p.parcelWaybillCode);
  }

  toggleParcelSelection(p: ViewParcels): void {
    if (!p.parcelWaybillCode) {
      return;
    }
    if (this.selectedWaybills.has(p.parcelWaybillCode)) {
      this.selectedWaybills.delete(p.parcelWaybillCode);
      this.allMatchingSelected = false;
    } else {
      this.selectedWaybills.set(p.parcelWaybillCode, p.waybillShipmentCode || '');
    }
  }

  areAllVisibleSelected(): boolean {
    return this.filteredParcels.length > 0 && this.filteredParcels.every(p =>
      !p.parcelWaybillCode || this.selectedWaybills.has(p.parcelWaybillCode)
    );
  }

  toggleSelectAll(): void {
    if (this.allMatchingSelected || this.areAllVisibleSelected()) {
      this.clearSelection();
      return;
    }
    this.selectAllMatching();
  }

  private selectAllMatching(): void {
    if (this.totalRecords === 0 || this.isSelectingAll) {
      return;
    }

    const body = this.buildSearchBody(1, Math.max(this.totalRecords, this.itemsPerPage));

    this.isSelectingAll = true;
    this.http.post<any>(`${environment.apiUrl}/overview-warehouse/parcels`, body).subscribe({
      next: (response) => {
        const all: ViewParcels[] = response?.parcels ?? [];
        all.forEach(p => {
          if (p.parcelWaybillCode) {
            this.selectedWaybills.set(p.parcelWaybillCode, p.waybillShipmentCode || '');
          }
        });
        this.allMatchingSelected = true;
        this.isSelectingAll = false;
        this.notificationService.success(
          `Selezionate ${this.selectedWaybills.size} bolle (tutti i colli filtrati)`
        );
      },
      error: (err) => {
        this.isSelectingAll = false;
        console.error('Errore durante la selezione di tutte le bolle:', err);
        this.notificationService.error('Errore durante la selezione di tutte le bolle');
      }
    });
  }

  clearSelection(): void {
    this.selectedWaybills.clear();
    this.allMatchingSelected = false;
  }

  printSelectedPDF(): void {
    const selectedWaybillCodes = Array.from(this.selectedWaybills.keys());

    if (selectedWaybillCodes.length === 0) {
      this.notificationService.warning('Nessuna bolla selezionata per la stampa PDF');
      return;
    }

    const waybillsMap: { key: string, value: string }[] = [];
    this.selectedWaybills.forEach((shipmentCode, waybillCode) => {
      waybillsMap.push({ key: waybillCode, value: shipmentCode });
    });

    this.printWaybillsPdf(waybillsMap);
  }

  downloadWaybillDocument(p: ViewParcels): void {
    if (!p.parcelWaybillCode) {
      this.notificationService.error('Bolla non trovata');
      return;
    }
    this.printWaybillsPdf([{ key: p.parcelWaybillCode, value: p.waybillShipmentCode || '' }]);
  }

  trackByWaybillCode(index: number, group: WaybillGroup): string {
    return group.waybillCode;
  }

  private rebuildWaybillGroups(): void {
    const groups = new Map<string, WaybillGroup>();

    for (const p of this.filteredParcels) {
      const code = p.parcelWaybillCode;
      if (!code) {
        continue;
      }

      let group = groups.get(code);
      if (!group) {
        group = {
          waybillCode: code,
          waybillOriginalCode: p.parcelOriginalWaybillCode || code,
          waybillShipmentCode: p.waybillShipmentCode || '',
          clientCode: p.parcelClientCode || '',
          clientName: p.clientName || '',
          dealerName: p.dealerName || '',
          dealerOperatingAddressStreet: p.dealerOperatingAddressStreet || '',
          dealerOperatingAddressCity: p.dealerOperatingAddressCity || '',
          parcelRegionName: p.parcelRegionName || '',
          parcelDate: p.parcelDate,
          parcelEstimatedDeliveryDate: p.parcelEstimatedDeliveryDate,
          parcelDeliveryDate: p.parcelDeliveryDate,
          waybillStatus: p.parcelWaybillStatus,
          ddtCodes: p.waybillDdtAttachments || '',
          totalWeight: 0,
          totalVolume: 0,
          parcelsCount: 0,
          parcels: [],
          waybillDdtSentDate: p.waybillDdtSentDate,
          waybillDownloadDdtDate: p.waybillDownloadDdtDate,
          waybillDownloadDdtBy: p.waybillDownloadDdtBy,
          parcelTransitStatus: p.parcelTransitStatus,
          parcelTourGroupName: p.parcelTourGroupName,
          parcelOriginalDeliveryDate: p.parcelOriginalDeliveryDate,
          parcelPickingEventStart: undefined,
          parcelShuttleArrivalDate: undefined
        };
        groups.set(code, group);
      }

      if (p.parcelPickingEventStart && (!group.parcelPickingEventStart || p.parcelPickingEventStart < group.parcelPickingEventStart)) {
        group.parcelPickingEventStart = p.parcelPickingEventStart;
      }

      if (p.parcelShuttleArrivalDate && (!group.parcelShuttleArrivalDate || p.parcelShuttleArrivalDate < group.parcelShuttleArrivalDate)) {
        group.parcelShuttleArrivalDate = p.parcelShuttleArrivalDate;
      }

      group.totalWeight += p.parcelWeight || 0;
      group.totalVolume += p.parcelVolume || 0;
      group.parcelsCount += 1;
      group.parcels.push(p);
    }

    groups.forEach((group, code) => {
      const serverTotals = this.serverWaybillTotals.get(code);
      if (serverTotals) {
        group.totalWeight = serverTotals.totalWeight;
        group.totalVolume = serverTotals.totalVolume;
        group.parcelsCount = serverTotals.parcelsCount;
      }
    });

    this.groupedWaybills = Array.from(groups.values());

    this.applyExpandModeToCurrentPage();
  }

  isWaybillGroupSelected(group: WaybillGroup): boolean {
    return this.selectedWaybills.has(group.waybillCode);
  }

  toggleWaybillGroupSelection(group: WaybillGroup): void {
    if (this.selectedWaybills.has(group.waybillCode)) {
      this.selectedWaybills.delete(group.waybillCode);
      this.allMatchingSelected = false;
    } else {
      this.selectedWaybills.set(group.waybillCode, group.waybillShipmentCode || '');
    }
  }

  areAllWaybillGroupsSelected(): boolean {
    const groups = this.groupedWaybills;
    return groups.length > 0 && groups.every(g => this.selectedWaybills.has(g.waybillCode));
  }

  getParcelTypeLabel(parcelType: string | undefined): string {
    if (!parcelType) {
      return '-';
    }
    switch (parcelType) {
      case 'PARCEL':
        return 'Collo';
      case 'PALLET':
        return 'Pallet';
      case 'S':
      case 'STK':
        return 'Stock';
      case 'U':
      case 'URG':
        return 'Urgente';
      default:
        return parcelType;
    }
  }

  getIntegerPart(value: number, digitsInfo: string): string {
    const formatted = formatNumber(value || 0, 'it', digitsInfo);
    const separatorIndex = formatted.lastIndexOf(',');
    return separatorIndex === -1 ? formatted : formatted.substring(0, separatorIndex);
  }

  getDecimalPart(value: number, digitsInfo: string): string {
    const formatted = formatNumber(value || 0, 'it', digitsInfo);
    const separatorIndex = formatted.lastIndexOf(',');
    return separatorIndex === -1 ? '' : formatted.substring(separatorIndex);
  }

  copyToClipboard(text: string, type: string): void {
    if (!text) {
      return;
    }
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        this.showCopySuccessToast(type, text);
      }).catch(err => {
        console.error('Failed to copy text: ', err);
        this.fallbackCopyTextToClipboard(text, type);
      });
    } else {
      this.fallbackCopyTextToClipboard(text, type);
    }
  }

  private fallbackCopyTextToClipboard(text: string, type: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showCopySuccessToast(type, text);
      } else {
        console.error('Fallback: Unable to copy');
        this.notificationService.error('Errore durante la copia negli appunti');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
      this.notificationService.error('Errore durante la copia negli appunti');
    }

    document.body.removeChild(textArea);
  }

  private showCopySuccessToast(type: string, text: string): void {
    const typeLabel = type === 'waybill' ? 'Codice Bolla' :
      type === 'ragione-sociale' ? 'Ragione Sociale' :
        type === 'indirizzo' ? 'Indirizzo' :
          type === 'città' ? 'Città' :
            type === 'regione' ? 'Regione' :
              type === 'cliente' ? 'Codice Cliente' :
                'Codice Collo';

    this.notificationService.success(`${typeLabel} "${text}" copiato negli appunti!`);
  }

  isWaybillExpanded(waybillCode: string): boolean {
    return this.expandedWaybillCodes.has(waybillCode);
  }

  toggleWaybillExpand(waybillCode: string): void {
    if (this.expandedWaybillCodes.has(waybillCode)) {
      this.expandedWaybillCodes.delete(waybillCode);
    } else {
      this.expandedWaybillCodes.add(waybillCode);
    }

    this.expandAllMode = null;
  }

  hasAnyWaybillExpanded(): boolean {
    return this.groupedWaybills.some(group =>
      this.expandedWaybillCodes.has(group.waybillCode)
    );
  }

  toggleAllWaybills(): void {
    const shouldExpand = !this.hasAnyWaybillExpanded();

    this.expandAllMode = shouldExpand;

    for (const group of this.groupedWaybills) {
      if (shouldExpand) {
        this.expandedWaybillCodes.add(group.waybillCode);
      } else {
        this.expandedWaybillCodes.delete(group.waybillCode);
      }
    }
  }

  private applyExpandModeToCurrentPage(): void {
    if (this.expandAllMode === true) {
      for (const group of this.groupedWaybills) {
        this.expandedWaybillCodes.add(group.waybillCode);
      }
    }

    if (this.expandAllMode === false) {
      for (const group of this.groupedWaybills) {
        this.expandedWaybillCodes.delete(group.waybillCode);
      }
    }
  }
}